'use client';

import { useState } from 'react';
import { motion } from 'framer-motion';
import {
  BarChart3, MessageCircle, Eye, Globe, Check, ArrowRight,
} from 'lucide-react';
import BookDemoSection from '@/components/BookDemoSection';
import { BookDemoButton, FaqAccordion } from '@/components/ui';
import { fadeUp, scaleIn, viewportOnce, spring, springSoft, staggerContainer } from '@/lib/motion';

const BRAND = '#0098FF';
const BRAND_LIGHT = '#7EC8FF';
const BRAND_SUBTLE = 'rgba(0,152,255,0.08)';
const BRAND_BORDER = 'rgba(0,152,255,0.18)';

const HERO_VALUES = [
  { lead: 'Resolve faster', text: 'AI-led triage routes high-impact conversations to the right agent automatically, cutting manual sorting.' },
  { lead: 'Detect risk earlier', text: 'spot emerging public narratives and sentiment spikes before they escalate into brand crises.' },
  { lead: 'Cut tool sprawl', text: 'replace your listening tool, engagement inbox, and ticketing layer with one platform and one vendor.' },
];

const WHY_SWITCH = [
  {
    num: '01',
    title: 'Tool consolidation',
    body: 'Listening, engagement, ticketing, and reporting were spread across multiple vendors. Locobuzz replaced them with one architecture and one renewal cycle.',
  },
  {
    num: '02',
    title: 'Speed to action',
    body: 'The gap between detecting a public risk and routing it to the right agent was measured in hours. With Locobuzz, that gap closes in minutes.',
  },
  {
    num: '03',
    title: 'Governance at scale',
    body: "Managing CX across multiple brands, regions, or BUs created accountability gaps. Locobuzz's role-based access and shared context fixed that without slowing down frontline teams.",
  },
];

const WORKFLOW = [
  {
    num: '01',
    engine: 'SignalSense™',
    title: 'Detect',
    body: 'Monitor public conversations, reviews, and digital mentions in real time across channels. SignalSense™ filters noise from signal, surfacing high-velocity narratives and sentiment shifts before they become brand risks, not after.',
  },
  {
    num: '02',
    engine: 'ContextualPulse™',
    title: 'Prioritise',
    body: 'Not every mention needs the same response. ContextualPulse™ scores incoming conversations by sentiment, influence, reach, and intent — so your team always knows what to act on first, without manual sorting or gut-feel triage.',
  },
  {
    num: '03',
    engine: 'ResponseGenie™',
    title: 'Resolve',
    body: 'High-priority conversations convert into structured cases automatically. ResponseGenie™ routes each case to the right agent or team, with full context attached — sentiment history, channel, public impact, and prior interactions — so resolution is faster and handoffs are clean.',
  },
  {
    num: '04',
    engine: 'AgentIQ™',
    title: 'Learn',
    body: 'Every resolved case feeds back into performance reporting. AgentIQ™ connects conversation drivers to resolution outcomes, giving CX leaders and ops teams the visibility to improve — not just measure.',
  },
];

const SEGMENTS = [
  {
    id: 'enterprise',
    tab: 'Enterprise CX Teams',
    title: 'For large teams managing high-volume, high-impact conversations',
    tldr: 'For large teams, the critical gap is speed-to-action. Locobuzz connects the listening signal to a prioritised case in real time, while most enterprise platforms require separate modules before teams can act.',
    rightHeader: 'Typical Enterprise Platforms',
    rows: [
      { criteria: 'Time-to-value', locobuzz: 'Listening and action workflows go live together, so teams start resolving issues early.', other: 'Rollouts begin in silos, with value unlocked only after multiple modules and integrations.' },
      { criteria: 'Signal-to-action speed', locobuzz: 'High-risk public conversations convert into prioritized cases in real time.', other: 'Insights sit in dashboards while action happens elsewhere, slowing response.' },
      { criteria: 'Operational efficiency', locobuzz: 'AI-led triage reduces manual sorting and duplicate ownership.', other: 'Automation is uneven across modules, leaving handoffs manual.' },
      { criteria: 'Risk & crisis containment', locobuzz: 'Early trend detection with built-in escalation workflows limits reputational impact.', other: 'Alerts exist, but coordinated response depends on external tools.' },
      { criteria: 'Governance at scale', locobuzz: 'Role-based access and multi-brand controls standardize CX without friction.', other: 'Governance is possible but becomes configuration-heavy across regions.' },
      { criteria: 'Cross-team alignment', locobuzz: 'Marketing, support, and ops act on shared context and outcomes.', other: 'Teams operate in separate modules, fragmenting accountability.' },
      { criteria: 'Business-linked insights', locobuzz: 'Reporting ties conversation drivers to sentiment and resolution outcomes.', other: 'Metrics often focus on activity volume rather than impact.' },
      { criteria: 'Tool sprawl cost', locobuzz: 'One platform replaces listening, engagement, and action layers.', other: 'Multiple products, renewals, and services stitched together.' },
      { criteria: 'Deployment resilience', locobuzz: 'Unified architecture ensures consistent capability across teams.', other: 'Multi-cloud stacks create uneven maturity and dependencies.' },
    ],
  },
  {
    id: 'growing',
    tab: 'Growing Teams',
    title: 'For teams scaling beyond inbox management',
    tldr: 'For teams scaling beyond inbox management, the difference is whether the platform grows with you or forces you to bolt on new tools as volume increases.',
    rightHeader: 'Engagement-led Platforms',
    rows: [
      { criteria: 'Smart prioritization', locobuzz: 'Teams focus first on impact, intent, and sentiment.', other: 'Every mention looks equal, increasing manual effort.' },
      { criteria: 'Scalable workflows', locobuzz: 'Adding agents and brands keeps ownership and SLAs consistent.', other: 'Scale adds queues and rules, not clarity.' },
      { criteria: 'Resolution depth', locobuzz: 'Conversations move through a structured case lifecycle.', other: 'Unified inboxes lack strong closure workflows.' },
      { criteria: 'Automation efficiency', locobuzz: 'AI routing reduces repetitive work and staffing pressure.', other: 'Rule-heavy automation increases maintenance overhead.' },
      { criteria: 'Leadership visibility', locobuzz: 'Leaders track issue drivers and closure outcomes.', other: 'Reporting centers on responses and engagement counts.' },
      { criteria: 'Growth path', locobuzz: 'Start with listening and expand into full CX intelligence seamlessly.', other: 'Teams outgrow tools and add separate systems later.' },
    ],
  },
  {
    id: 'service',
    tab: 'Service-First Teams',
    title: 'For ticketing-first teams expanding into social',
    tldr: 'For ticketing-first teams, the gap is in how social signals enter the workflow — as first-class inputs with public risk context, or as late-arriving connectors flattened into standard tickets.',
    rightHeader: 'Ticketing-first Platforms',
    rows: [
      { criteria: 'Early issue capture', locobuzz: 'Social signals are treated as first-class CX inputs.', other: 'Social enters late via connectors or manual logging.' },
      { criteria: 'Public risk handling', locobuzz: 'Public impact is prioritized alongside SLA-based resolution.', other: 'Public context is flattened into standard tickets.' },
      { criteria: 'Native listening', locobuzz: 'Built-in monitoring removes dependency on extra tools.', other: 'Listening is an add-on with added cost.' },
      { criteria: 'Resolution context', locobuzz: 'Sentiment, influence, and history speed smarter escalation.', other: 'Context remains ticket-centric and limited.' },
      { criteria: 'Unified customer view', locobuzz: 'One view across social, reviews, chat, and digital touchpoints.', other: 'Channels remain segmented, repeating effort.' },
      { criteria: 'Actionable insights', locobuzz: 'Ops performance is linked to reputation and sentiment drivers.', other: 'Reporting focuses on ticket throughput alone.' },
    ],
  },
];

const AUDIENCE_SOLUTIONS = [
  {
    Icon: BarChart3,
    audience: 'For CX Leaders',
    bullets: [
      'Get early warning signals before issues impact NPS, churn, or brand trust.',
      "See what's driving sentiment shifts and whether teams are closing the loop.",
      'Standardize governance across brands/regions without slowing down frontline teams.',
    ],
  },
  {
    Icon: MessageCircle,
    audience: 'For Customer Support Teams',
    bullets: [
      'Reduce manual triage with AI-assisted categorization and routing.',
      'Resolve faster with richer context (history, sentiment, public impact).',
      'Escalate the right issues early — fewer back-and-forth handoffs.',
    ],
  },
  {
    Icon: Globe,
    audience: 'For Marketing & Brand Teams',
    bullets: [
      'Track reputation and campaign impact in real time, not post-mortem.',
      'Coordinate with support on public responses with shared context and approvals.',
      'Turn voice-of-customer insights into product and messaging improvements.',
    ],
  },
  {
    Icon: Eye,
    audience: 'For Digital & Reputation Teams',
    bullets: [
      'Spot spikes and emerging narratives early, across channels and communities.',
      'Separate noise from high-impact conversations using influence + velocity signals.',
      'Run crisis workflows with clarity on ownership, status, and resolution.',
    ],
  },
];

const FAQS = [
  { q: 'What is the difference between a social listening tool and a unified CX platform?', a: "A social listening tool monitors mentions and sentiment. A unified CX platform goes further — it connects that listening signal to triage, resolution workflows, and cross-team reporting. Locobuzz is built as the latter, so teams don't just see what's happening; they can act on it without switching tools." },
  { q: 'Which teams benefit most from a unified CX platform?', a: "CX leadership, customer support, marketing, digital, and operations teams all benefit — because they're all acting on the same customer signals. A unified platform gives every team shared context, so support isn't resolving blind, marketing isn't reporting in a vacuum, and leadership isn't waiting for weekly dashboards." },
  { q: 'How should a CX platform handle public complaints differently from private support tickets?', a: 'Public complaints carry reputational risk — they need to be prioritised by impact and visibility, not just SLA. Private interactions follow structured resolution workflows. A platform that treats both the same will either over-escalate tickets or under-respond in public. Locobuzz applies separate prioritisation logic to each.' },
  { q: 'What should I look for when comparing enterprise CX platforms?', a: 'Look for four things: how fast the platform converts a listening signal into a prioritised action, whether workflows scale without adding manual rules, how governance works across brands and regions, and whether reporting ties to outcomes — not just activity volume. Tool consolidation and time-to-value should also factor in.' },
  { q: 'How long does it take to see value from a new CX platform?', a: 'With platforms that require multiple module rollouts and integrations, value is often delayed by weeks or months. With Locobuzz, listening and action workflows go live together — so teams start detecting and resolving issues in the same deployment cycle, not after a phased integration roadmap.' },
  { q: 'Can a CX platform replace both a social listening tool and a ticketing system?', a: "Yes — if it's built with native listening, structured case workflows, and shared context across both layers. Most platforms do one well and bolt on the other. Locobuzz is architected to handle both natively, which removes the integration dependency and reduces tool sprawl for growing and enterprise teams alike." },
];

function SegmentTable({ title, tldr, leftHeader, rightHeader, rows }) {
  return (
    <div>
      <h3 style={{ fontSize: 'clamp(1.25rem,2.4vw,1.6rem)', fontWeight: 700, color: 'var(--text-primary)', marginBottom: 16 }}>
        {title}
      </h3>
      <p style={{
        fontSize: 15, color: 'var(--text-secondary)', lineHeight: 1.65, marginBottom: 28,
        background: BRAND_SUBTLE, borderLeft: `3px solid ${BRAND}`,
        padding: '12px 16px', borderRadius: '0 8px 8px 0',
      }}>
        {tldr}
      </p>

      <div style={{ border: '1px solid var(--border)', borderRadius: 16, overflow: 'hidden', background: 'var(--bg-page)' }}>
        {/* header row */}
        <div style={{
          display: 'grid', gridTemplateColumns: 'minmax(120px,0.8fr) 1fr 1fr',
          background: 'var(--bg-card)', borderBottom: '1px solid var(--border)',
        }}>
          <div style={{ padding: '14px 18px', fontSize: 13, fontWeight: 600, color: 'var(--text-muted)' }}>Criteria</div>
          <div style={{ padding: '14px 18px', fontSize: 13, fontWeight: 700, color: BRAND, borderLeft: `1px solid var(--border)` }}>{leftHeader}</div>
          <div style={{ padding: '14px 18px', fontSize: 13, fontWeight: 600, color: 'var(--text-muted)', borderLeft: '1px solid var(--border)' }}>{rightHeader}</div>
        </div>

        <motion.div variants={staggerContainer(0.06)} initial="hidden" whileInView="show" viewport={viewportOnce}>
          {rows.map(({ criteria, locobuzz, other }, i) => (
            <motion.div
              key={criteria}
              variants={fadeUp}
              transition={springSoft}
              style={{
                display: 'grid', gridTemplateColumns: 'minmax(120px,0.8fr) 1fr 1fr',
                background: i % 2 === 0 ? 'var(--bg-page)' : 'var(--bg-card)',
                borderBottom: i === rows.length - 1 ? 'none' : '1px solid var(--border)',
              }}
            >
              <div style={{ padding: '15px 18px', fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>{criteria}</div>
              <div style={{ padding: '15px 18px', fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.55, borderLeft: '1px solid var(--border)', display: 'flex', gap: 9, alignItems: 'flex-start' }}>
                <Check size={15} style={{ color: BRAND, flexShrink: 0, marginTop: 3 }} />
                <span>{locobuzz}</span>
              </div>
              <div style={{ padding: '15px 18px', fontSize: 14, color: 'var(--text-muted)', lineHeight: 1.55, borderLeft: '1px solid var(--border)' }}>{other}</div>
            </motion.div>
          ))}
        </motion.div>
      </div>

      <div style={{ marginTop: 24 }}>
        <BookDemoButton size="md" label="See Locobuzz in Action" />
      </div>
    </div>
  );
}

export default function VsOtherPlatformsPage() {
  const [active, setActive] = useState(SEGMENTS[0].id);
  const activeSegment = SEGMENTS.find((s) => s.id === active) ?? SEGMENTS[0];

  return (
    <>
      {/* ── 1. First fold / Hero ── */}
      <section style={{
        background: 'linear-gradient(135deg, #060d1f 0%, #0b1930 60%, #0d2248 100%)',
        padding: '100px 24px 80px',
        textAlign: 'center',
        position: 'relative',
        overflow: 'hidden',
      }}>
        <div style={{
          position: 'absolute', inset: 0,
          background: 'radial-gradient(ellipse 80% 60% at 50% 0%, rgba(0,152,255,0.18) 0%, transparent 70%)',
          pointerEvents: 'none',
        }} />
        <div style={{ maxWidth: 880, margin: '0 auto', position: 'relative' }}>
          <motion.div variants={fadeUp} initial="hidden" animate="show" transition={spring}>
            <span style={{
              display: 'inline-block', background: BRAND_SUBTLE,
              border: `1px solid ${BRAND_BORDER}`, color: BRAND,
              fontSize: 12, fontWeight: 600, letterSpacing: '0.1em',
              textTransform: 'uppercase', padding: '6px 16px', borderRadius: 100, marginBottom: 24,
            }}>Platform Comparison</span>
          </motion.div>

          <motion.h1
            variants={fadeUp} initial="hidden" animate="show" transition={{ ...spring, delay: 0.05 }}
            style={{ fontSize: 'clamp(2rem,5vw,3rem)', fontWeight: 800, color: '#fff', lineHeight: 1.18, marginBottom: 20 }}
          >
            The <span style={{ color: BRAND_LIGHT }}>Smarter</span> Way to Compare CX Platforms
          </motion.h1>

          <motion.p
            variants={fadeUp} initial="hidden" animate="show" transition={{ ...spring, delay: 0.1 }}
            style={{ fontSize: 17, color: 'rgba(255,255,255,0.72)', lineHeight: 1.65, maxWidth: 720, margin: '0 auto 36px' }}
          >
            Most CX platforms are built for one layer — listening, ticketing, or engagement. Locobuzz connects all three in a single unified platform, so your teams detect risk early, prioritize by impact, and close the loop without switching tools or waiting on integrations.
          </motion.p>

          <motion.ul
            variants={staggerContainer(0.08)} initial="hidden" animate="show"
            style={{ listStyle: 'none', padding: 0, margin: '0 auto 40px', maxWidth: 620, display: 'flex', flexDirection: 'column', gap: 12, textAlign: 'left' }}
          >
            {HERO_VALUES.map((v) => (
              <motion.li
                key={v.lead} variants={fadeUp} transition={springSoft}
                style={{ display: 'flex', gap: 12, alignItems: 'flex-start', fontSize: 15, color: 'rgba(255,255,255,0.86)', lineHeight: 1.5 }}
              >
                <span style={{
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  width: 22, height: 22, borderRadius: '50%', background: BRAND, flexShrink: 0, marginTop: 1,
                }}>
                  <Check size={12} color="#fff" />
                </span>
                <span><strong style={{ color: '#fff', fontWeight: 700 }}>{v.lead}</strong> — {v.text}</span>
              </motion.li>
            ))}
          </motion.ul>

          <motion.div
            variants={fadeUp} initial="hidden" animate="show" transition={{ ...spring, delay: 0.18 }}
            style={{ display: 'flex', gap: 16, justifyContent: 'center', flexWrap: 'wrap' }}
          >
            <BookDemoButton size="lg" label="Book a free demo" />
            <a href="#compare" style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              padding: '14px 28px', borderRadius: 8, fontWeight: 600, fontSize: 16,
              border: '1.5px solid rgba(255,255,255,0.22)', color: '#fff', textDecoration: 'none',
            }}>
              See the Comparison <ArrowRight size={16} />
            </a>
          </motion.div>

          <motion.div
            variants={fadeUp} initial="hidden" animate="show" transition={{ ...spring, delay: 0.24 }}
            style={{
              maxWidth: 720, margin: '40px auto 0', textAlign: 'left',
              background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)',
              borderLeft: `4px solid ${BRAND}`, borderRadius: 12, padding: '20px 24px',
            }}
          >
            <h3 style={{ fontSize: 13, fontWeight: 700, color: BRAND_LIGHT, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>
              What is a unified CX platform?
            </h3>
            <p style={{ fontSize: 15, color: 'rgba(255,255,255,0.72)', lineHeight: 1.7 }}>
              A unified CX platform combines social listening, sentiment analysis, workflow-based resolution, and cross-team reporting in one system. Unlike point solutions that handle one layer, a unified platform lets CX, support, marketing, and digital teams act on the same customer signal — from detection to closure.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ── 2. Why teams switch ── */}
      <section style={{ padding: '72px 24px', background: 'var(--bg-card)', borderBottom: '1px solid var(--border)' }}>
        <div style={{ maxWidth: 1040, margin: '0 auto' }}>
          <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={viewportOnce} transition={spring}>
            <span style={{
              display: 'inline-block', background: BRAND_SUBTLE, border: `1px solid ${BRAND_BORDER}`, color: BRAND,
              fontSize: 12, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase',
              padding: '6px 16px', borderRadius: 100, marginBottom: 16,
            }}>Why teams switch</span>
            <h2 style={{ fontSize: 'clamp(1.5rem,3vw,2.1rem)', fontWeight: 700, color: 'var(--text-primary)', marginBottom: 14 }}>
              Built for teams that have outgrown point solutions
            </h2>
            <p style={{ fontSize: 16, color: 'var(--text-muted)', maxWidth: 720, lineHeight: 1.65, marginBottom: 40 }}>
              Teams switch to Locobuzz when the gap between detecting a customer risk and acting on it becomes a business problem. Listening, ticketing, and reporting were three separate vendors, three separate contracts, and three separate workflows. Locobuzz replaces that stack with one architecture, shared context, and a single source of truth for CX performance.
            </p>
          </motion.div>

          <motion.div
            variants={staggerContainer(0.1)} initial="hidden" whileInView="show" viewport={viewportOnce}
            style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 20 }}
          >
            {WHY_SWITCH.map(({ num, title, body }) => (
              <motion.div
                key={num}
                variants={scaleIn}
                transition={springSoft}
                style={{ background: 'var(--bg-page)', border: '1px solid var(--border)', borderRadius: 16, padding: '24px' }}
              >
                <div style={{
                  fontSize: '1.9rem', fontWeight: 800, lineHeight: 1, marginBottom: 12,
                  color: 'transparent', WebkitTextStroke: `2px ${BRAND}`,
                }}>{num}</div>
                <h4 style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 8 }}>{title}</h4>
                <p style={{ fontSize: 14, color: 'var(--text-muted)', lineHeight: 1.6 }}>{body}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── 3. Segment comparisons ── */}
      <section id="compare" style={{ padding: '80px 24px', background: 'var(--bg-page)' }}>
        <div style={{ maxWidth: 1040, margin: '0 auto' }}>
          <motion.div
            variants={fadeUp} initial="hidden" whileInView="show" viewport={viewportOnce} transition={spring}
            style={{ textAlign: 'center', marginBottom: 44 }}
          >
            <h2 style={{ fontSize: 'clamp(1.5rem,3vw,2.25rem)', fontWeight: 700, color: 'var(--text-primary)', marginBottom: 14 }}>
              How to Choose a CX Platform: Enterprise, Growth, and Service-First
            </h2>
            <p style={{ fontSize: 16, color: 'var(--text-muted)', maxWidth: 680, margin: '0 auto', lineHeight: 1.6 }}>
              A practical, decision-maker comparison — what you get in day-to-day outcomes, and what typically becomes a hidden cost or constraint with other approaches.
            </p>
          </motion.div>

          {/* segment switcher */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, justifyContent: 'center', marginBottom: 40 }}>
            {SEGMENTS.map((seg) => {
              const isActive = seg.id === active;
              return (
                <button
                  key={seg.id}
                  type="button"
                  onClick={() => setActive(seg.id)}
                  style={{
                    cursor: 'pointer',
                    padding: '10px 20px', borderRadius: 100, fontSize: 14, fontWeight: 600,
                    border: `1px solid ${isActive ? BRAND : 'var(--border)'}`,
                    background: isActive ? BRAND : 'var(--bg-card)',
                    color: isActive ? '#fff' : 'var(--text-secondary)',
                    transition: 'all 0.15s ease',
                  }}
                >
                  {seg.tab}
                </button>
              );
            })}
          </div>

          <motion.div
            key={activeSegment.id}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={springSoft}
          >
            <SegmentTable
              title={activeSegment.title}
              tldr={activeSegment.tldr}
              leftHeader="With Locobuzz"
              rightHeader={activeSegment.rightHeader}
              rows={activeSegment.rows}
            />
          </motion.div>
        </div>
      </section>

      {/* ── 4. How it works / workflow (dark) ── */}
      <section id="how-it-works" style={{ padding: '88px 24px', background: 'linear-gradient(135deg, #060d1f 0%, #0b1930 60%, #0d2248 100%)' }}>
        <div style={{ maxWidth: 1080, margin: '0 auto' }}>
          <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={viewportOnce} transition={spring}>
            <span style={{
              display: 'inline-block', background: BRAND_SUBTLE, border: `1px solid ${BRAND_BORDER}`, color: BRAND_LIGHT,
              fontSize: 12, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase',
              padding: '6px 16px', borderRadius: 100, marginBottom: 16,
            }}>How it works · AI-powered</span>
            <h2 style={{ fontSize: 'clamp(1.6rem,3vw,2.2rem)', fontWeight: 700, color: '#fff', lineHeight: 1.2, marginBottom: 16 }}>
              From Social Listening to Case Resolution in One Workflow
            </h2>
            <p style={{ fontSize: 16, color: 'rgba(255,255,255,0.65)', maxWidth: 720, lineHeight: 1.65, marginBottom: 28 }}>
              Most CX stacks are built in layers — one tool listens, another tickets, a third reports. Locobuzz collapses those layers into a single workflow, so the signal that triggers an alert is the same signal that opens a case, routes it to the right agent, and feeds into your executive dashboard.
            </p>
            <div style={{
              background: 'rgba(0,152,255,0.12)', border: '1px solid rgba(0,152,255,0.25)', borderRadius: 12,
              padding: '14px 20px', fontSize: 15, color: 'rgba(255,255,255,0.82)', lineHeight: 1.6, marginBottom: 36,
            }}>
              Locobuzz runs on a <strong style={{ color: BRAND_LIGHT }}>four-stage workflow — Detect, Prioritise, Resolve, and Learn</strong> — each powered by a dedicated AI engine, all running on shared customer context.
            </div>
          </motion.div>

          <motion.div
            variants={staggerContainer(0.1)} initial="hidden" whileInView="show" viewport={viewportOnce}
            style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: 16, marginBottom: 28 }}
          >
            {WORKFLOW.map(({ num, engine, title, body }) => (
              <motion.div
                key={num}
                variants={fadeUp}
                transition={springSoft}
                style={{
                  background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.09)',
                  borderRadius: 16, padding: '26px 22px',
                }}
              >
                <div style={{
                  fontSize: '2.2rem', fontWeight: 800, lineHeight: 1, marginBottom: 14,
                  color: 'transparent', WebkitTextStroke: '1px rgba(255,255,255,0.22)',
                }}>{num}</div>
                <span style={{
                  display: 'inline-block', fontSize: 11, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase',
                  color: BRAND_LIGHT, background: 'rgba(0,152,255,0.18)', padding: '4px 10px', borderRadius: 100, marginBottom: 12,
                }}>{engine}</span>
                <h4 style={{ fontSize: 17, fontWeight: 700, color: '#fff', marginBottom: 10 }}>{title}</h4>
                <p style={{ fontSize: 13.5, color: 'rgba(255,255,255,0.6)', lineHeight: 1.6 }}>{body}</p>
              </motion.div>
            ))}
          </motion.div>

          <motion.div
            variants={fadeUp} initial="hidden" whileInView="show" viewport={viewportOnce} transition={spring}
            style={{
              background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 12,
              padding: '16px 22px', fontSize: 15, color: 'rgba(255,255,255,0.7)', lineHeight: 1.6, textAlign: 'center',
            }}
          >
            Each stage is designed to serve a different team — but <strong style={{ color: '#fff' }}>all four run on shared context</strong>, so there&apos;s no gap between what&apos;s detected and what gets done.
          </motion.div>
        </div>
      </section>

      {/* ── 5. Team grid ── */}
      <section id="who-uses" style={{ padding: '80px 24px', background: 'var(--bg-card)' }}>
        <div style={{ maxWidth: 1120, margin: '0 auto' }}>
          <motion.div
            variants={fadeUp} initial="hidden" whileInView="show" viewport={viewportOnce} transition={spring}
            style={{ textAlign: 'center', marginBottom: 52 }}
          >
            <h2 style={{ fontSize: 'clamp(1.5rem,3vw,2rem)', fontWeight: 700, color: 'var(--text-primary)', marginBottom: 14 }}>
              Who Uses Locobuzz: CX, Support, Marketing, Digital, and Operations Teams
            </h2>
            <p style={{ fontSize: 16, color: 'var(--text-muted)', maxWidth: 720, margin: '0 auto', lineHeight: 1.6 }}>
              Customer experience is shaped across leadership, support, digital, operations, marketing, and technology. Locobuzz helps each team act on the same reality — so decisions are faster, execution is cleaner, and outcomes are measurable.
            </p>
          </motion.div>

          <motion.div
            variants={staggerContainer(0.1)} initial="hidden" whileInView="show" viewport={viewportOnce}
            style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 24 }}
          >
            {AUDIENCE_SOLUTIONS.map(({ Icon, audience, bullets }) => (
              <motion.div
                key={audience}
                variants={scaleIn}
                transition={springSoft}
                style={{
                  background: 'var(--bg-page)', border: '1px solid var(--border)',
                  borderRadius: 16, padding: '28px 24px',
                }}
              >
                <div style={{
                  width: 44, height: 44, borderRadius: 12,
                  background: BRAND_SUBTLE, border: `1px solid ${BRAND_BORDER}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14,
                }}>
                  <Icon size={20} color={BRAND} />
                </div>
                <h3 style={{ fontSize: 15, fontWeight: 700, color: BRAND, marginBottom: 14 }}>{audience}</h3>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {bullets.map((b) => (
                    <li key={b} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                      <Check size={14} style={{ color: BRAND, flexShrink: 0, marginTop: 2 }} />
                      {b}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </motion.div>

          <motion.div
            variants={fadeUp} initial="hidden" whileInView="show" viewport={viewportOnce} transition={spring}
            style={{ textAlign: 'center', marginTop: 48 }}
          >
            <BookDemoButton size="lg" label="Get a Live Walkthrough" />
          </motion.div>
        </div>
      </section>

      {/* ── 6. Final CTA band ── */}
      <section style={{ padding: '72px 24px 80px', background: 'var(--bg-page)' }}>
        <motion.div
          variants={fadeUp} initial="hidden" whileInView="show" viewport={viewportOnce} transition={spring}
          style={{ maxWidth: 760, margin: '0 auto', textAlign: 'center' }}
        >
          <h2 style={{ fontSize: 'clamp(1.4rem,2.8vw,2rem)', fontWeight: 700, color: 'var(--text-primary)', lineHeight: 1.35, marginBottom: 16 }}>
            See how listening, engagement, workflows, and CX intelligence come together.
          </h2>
          <p style={{ fontSize: 16, color: 'var(--text-muted)', maxWidth: 520, margin: '0 auto 32px', lineHeight: 1.65 }}>
            So your teams can do more with less — one platform, one source of truth, zero tool sprawl.
          </p>
          <BookDemoButton size="lg" label="Book a Free Demo" />
        </motion.div>
      </section>
      <BookDemoSection />

      {/* ── 5. FAQ ── */}
      <section style={{ padding: '80px 24px', background: 'var(--bg-card)' }}>
        <div style={{ maxWidth: 1000, margin: '0 auto' }}>
          <h2 style={{ fontSize: 'clamp(1.25rem,2.5vw,1.75rem)', fontWeight: 700, color: 'var(--text-primary)', textAlign: 'center', marginBottom: 40 }}>
            Frequently Asked Questions
          </h2>
          <FaqAccordion items={FAQS} columns={2} />
        </div>
      </section>
    </>
  );
}
